import React from 'react'
import styled from 'styled-components'
import IEventedComponent, { messageTypes } from '../components/IEventedComponent'
import {
  maxSidebarWidth,
  MobileDeviceWidth,
  TabletDeviceWidth,
  tabletMaxSidebarWidth,
  warningMessages
} from '../lib/constants'
import colours from '../lib/colours'
import SearchControl from './search-control'
import NvraAreaWarning from '../content/nvra-area-search-warning.mdx'
import NvraAreaSelectedWarning from '../content/nvra-area-selected-warning.mdx'
import CrownLandSelectedWarning from '../content/crown-land-selected-warning.mdx'
import ExistingPaymentWarning from '../content/existing-payment-warning.mdx'
import TwelvePrecinctsWarning from '../content/int-precincts-warning.mdx'
import OutsideMsaLevyAreaWarning from '../content/outside-msa-levy-area-warning.mdx'
import ConservationAreaWarning from '../content/conservation-area-warning.mdx'
import Callout from '../components/callout'
import SectionTitle from '../components/section-title'
import Table, { ITableRowData } from '../components/table'
import Spinner from '../components/spinner'
import CloseButton from '../components/close-button'
import {
  ParcelRemovedEvent,
  OpenFileUploadDialogEvent,
  UploadAreaRemovedEvent,
  DisableParcelSelectionModeEvent,
  EnableParcelSelectionModeEvent,
  ParcelMouseEnterEvent,
  ParcelMouseLeaveEvent
} from '../lib/messages'
import Dialog from './dialog'
import DetailsView from './details-view'
import BackButton from '../components/back-button'
import LevyLiability from './levy-liability'
import ConservationAreas from './conservation-areas'
import ReadMoreButton from '../components/read-more-button'

const SidebarDiv = styled.div`
  flex-direction: column;
  position: relative;
  height: 100%;
  font-weight: lighter;
  color: ${colours.darkPurple};
  max-width: 90%;
  flex: 0 0 ${maxSidebarWidth}px;
  background-color: ${colours.white};
  @media (max-width: ${TabletDeviceWidth}px) {
    flex: 0 0 ${tabletMaxSidebarWidth}px;
  }
  @media (max-width: ${MobileDeviceWidth}px) {
    flex: unset;
    flex-grow: 1;
    flex-shrink: 0;
    overflow: auto;
    height: 60%;
    max-width: 100%;
    order: 2;
  }
`

const SearchControlContainer = styled.div`
  padding: 24px;
  background-color: ${colours.mediumPurple};
  @media (max-width: ${MobileDeviceWidth}px) {
    background-color: unset;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 auto;
  align-items: center;
  justify-content: center;
`

const MainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 auto;
  overflow: auto;
  padding-bottom: 10px;
  @media (max-width: ${MobileDeviceWidth}px) {
    flex-grow: 1;
    flex-shrink: 0;
  }
`

const DataContainer = styled.div`
  flex: 1 0 auto;
  padding: 0 24px;
  margin-top: -10px;
`

const ClearButtonContainer = styled.div`
  padding: 5px 24px;
`

const MainButton = styled.button`
  padding: 23px 24px;
  background-color: ${colours.aqua};
  color: ${colours.darkPurple};
  font-weight: 600;
  border: none;
  transition: background-color 0.6s linear;
  &:hover {
    background-color: #009ea4;
  }
`

const UploadShapefile = styled.span`
  font-weight: 600;
  cursor: pointer;
`

const AreaWarningContainer = styled.div`
  font-weight: 300;
`

interface IProps {
  searchLocation: any
  parcelsSelected: string[]
  parcelData: any
  parcelAggregates: any
  areaData: any
  rates: any
  fetching: boolean
  showOutsideMsaWarning: boolean
  setShowOutsideMsaWarning: any
  setInfoPopupState: any
}

interface IState {
  isOpen: boolean
  detailsWindowOpen: boolean
  isParcelSelectionModeEnabled: boolean
  isParcelSelectionEnabled: boolean
}

const SidebarControl = class extends IEventedComponent<IProps, IState> {
  state = {
    isOpen: true,
    detailsWindowOpen: false,
    isParcelSelectionModeEnabled: true,
    isParcelSelectionEnabled: true
  }

  componentDidMount() {
    this.subscribe(messageTypes.onSidebarToggled, this.handleSidebarToggled.bind(this))
    this.subscribe(messageTypes.onEnableParcelSelection, () => this.setState({ isParcelSelectionEnabled: true }))
    this.subscribe(messageTypes.onDisableParcelSelection, () => this.setState({ isParcelSelectionEnabled: false }))
    this.subscribe(messageTypes.onEnableParcelSelectionMode, () =>
      this.setState({ isParcelSelectionModeEnabled: true })
    )
    this.subscribe(messageTypes.onDisableParcelSelectionMode, () =>
      this.setState({ isParcelSelectionModeEnabled: false })
    )
  }

  handleSidebarToggled = () => {
    this.setState((state: any, props: any) => ({
      isOpen: !state.isOpen
    }))
  }

  onUploadShapefileClick = () => {
    this.publish<OpenFileUploadDialogEvent>(messageTypes.onOpenFileUploadDialog, {})
    this.publish<DisableParcelSelectionModeEvent>(messageTypes.onDisableParcelSelectionMode, {})
  }

  setDetailsWindowOpen = (open: boolean) => {
    this.setState(() => ({
      detailsWindowOpen: open
    }))
  }

  renderParcelRemoveButton(parcelSpi: string) {
    return (
      <CloseButton
        fontSize="18px"
        onClick={() =>
          this.publish<ParcelRemovedEvent>(messageTypes.onParcelRemoved, {
            parcelSpi
          })
        }
      />
    )
  }

  clearAllParcels = () => {
    const { parcelsSelected } = this.props
    parcelsSelected.forEach(parcelSpi => {
      this.publish<ParcelRemovedEvent>(messageTypes.onParcelRemoved, {
        parcelSpi
      })
    })
  }

  clearUploadArea = () => {
    this.publish<UploadAreaRemovedEvent>(messageTypes.onUploadAreaRemoved, {})
    this.publish<EnableParcelSelectionModeEvent>(messageTypes.onEnableParcelSelectionMode, {})
  }

  getAreaWarnings = (areaData: any, areaType: string) => {
    const { setInfoPopupState } = this.props
    let warnings = []
    if (areaData.intNonUnpaid) {
      warnings.push([
        warningMessages.nonUnpaid.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, ExistingPaymentWarning)
      ])
    }
    if (areaData.intCrownLand) {
      warnings.push([
        warningMessages.crownLand.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, CrownLandSelectedWarning)
      ])
    }
    if (areaData.conservationAreas.length > 0) {
      warnings.push([
        warningMessages.conservationArea.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, ConservationAreaWarning)
      ])
    }
    if (areaData.intNativeVegRegulationsArea) {
      warnings.push([
        warningMessages.nativeVegRegulationsArea.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, NvraAreaSelectedWarning)
      ])
    }
    if (areaData.intPrecincts) {
      warnings.push([
        warningMessages.twelvePrecincts.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, TwelvePrecinctsWarning)
      ])
    }
    if (areaData.levyLand === 0 && !areaData.intPrecincts) {
      warnings.push([
        warningMessages.outsideMsaLevyArea.replace('%REPLACE_TYPE%', areaType),
        () => setInfoPopupState(true, OutsideMsaLevyAreaWarning)
      ])
    }
    return warnings
  }

  renderSelectionArea() {
    const { parcelsSelected, parcelData, areaData } = this.props
    const parcelTableRows: ITableRowData[] = parcelsSelected.map(x => {
      return {
        rowData: [x, parcelData[x].address, this.renderParcelRemoveButton(x)],
        warningData: this.getAreaWarnings(parcelData[x], 'parcel')
      }
    })
    return parcelsSelected.length ? (
      <Table
        headers={['Parcel SPI', 'Address', '']}
        rows={parcelTableRows}
        onRowMouseEnter={(x: string) =>
          this.publish<ParcelMouseEnterEvent>(messageTypes.onParcelMouseEnter, {
            parcelSpi: x
          })
        }
        onRowMouseLeave={(x: string) =>
          this.publish<ParcelMouseLeaveEvent>(messageTypes.onParcelMouseLeave, {
            parcelSpi: x
          })
        }
      />
    ) : (
      <>
        <p style={{ fontWeight: 300 }}>{`${areaData.totalArea} ha`}</p>
        {this.getAreaWarnings(areaData, 'area').map((x: any[]) => {
          return (
            <AreaWarningContainer key={x[0]}>
              <Callout type="warning">
                {x[0]}
                <ReadMoreButton label="Read more" onClick={x[1]} fontSize="16px" />
              </Callout>
            </AreaWarningContainer>
          )
        })}
      </>
    )
  }

  renderSelectionDetails() {
    const { parcelsSelected, fetching } = this.props
    return (
      <DataContainer>
        <SectionTitle title={parcelsSelected.length ? 'Selected parcels' : 'Selected area'} />
        {fetching ? (
          <p>fetching...</p>
        ) : (
          <>
            {this.renderSelectionArea()}
            <LevyLiability {...this.props} />
            <ConservationAreas {...this.props} />
          </>
        )}
      </DataContainer>
    )
  }

  renderMainContent() {
    const { parcelsSelected, areaData } = this.props
    const noData: boolean = !parcelsSelected.length && !areaData
    if (noData)
      return (
        <Callout type="action">
          <p>Select parcels from the map</p>

          <p>OR</p>

          <p>
            <UploadShapefile onClick={this.onUploadShapefileClick}>Upload a shapefile</UploadShapefile> to define your
            area of Crown Land or land being acquired through a s.35 subdivision
          </p>
          
          <p> 
            For detailed instructions refer to: 
          </p>

          <p> 
            How do I calculate a levy estimate in the MSA Levy Area?
          </p>
        </Callout>
      )
    return this.renderSelectionDetails()
  }

  render() {
    const { parcelsSelected, fetching, showOutsideMsaWarning, setShowOutsideMsaWarning, areaData, rates } = this.props
    const sidebarStyle = this.state.isOpen ? { display: 'flex' } : { display: 'none' }
    const noData: boolean = !parcelsSelected.length && !areaData

    return (
      <SidebarDiv style={sidebarStyle}>
        <SearchControlContainer>
          <SearchControl />
        </SearchControlContainer>
        {showOutsideMsaWarning && (
          <Callout type="warning" showDismissButton={true} onDismiss={() => setShowOutsideMsaWarning(false)}>
            <NvraAreaWarning />
          </Callout>
        )}
        {!noData && !fetching && (
          <ClearButtonContainer>
            <BackButton
              label="Clear selection"
              onClick={parcelsSelected.length ? this.clearAllParcels : this.clearUploadArea}
            />
          </ClearButtonContainer>
        )}
        <MainContentContainer>
          {fetching ? (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          ) : (
            this.renderMainContent()
          )}
        </MainContentContainer>
        {!noData && !fetching && <MainButton onClick={() => this.setDetailsWindowOpen(true)}>View Details</MainButton>}
        <Dialog open={this.state.detailsWindowOpen} showHeader={false} showFooter={false}>
          {!noData && <DetailsView onClose={() => this.setDetailsWindowOpen(false)} {...this.props} />}
        </Dialog>
      </SidebarDiv>
    )
  }
}

export default SidebarControl
